/**
 * Pure MENU navigation math — the cursor-movement logic for the classic vertical
 * menu (the ▶ selection cursor). Free of Phaser so it is unit-testable and shared.
 *
 * The classic Pokémon menus WRAP (pressing Up on the top option lands on the bottom
 * one, and vice-versa), which feels good on a d-pad/keyboard. Disabled items are
 * skipped so the cursor never parks on an unusable row.
 */

export type MenuDir = -1 | 1;

/**
 * Move the cursor from `current` by `dir` (-1 = up, +1 = down) over `count` items,
 * wrapping around the ends and skipping any indices in `disabled`. Returns the next
 * selectable index, or the current index if nothing is selectable.
 */
export function moveCursor(
  current: number,
  dir: MenuDir,
  count: number,
  disabled: ReadonlySet<number> = new Set(),
): number {
  if (count <= 0) return 0;
  if (disabled.size >= count) return clampIndex(current, count);
  let next = clampIndex(current, count);
  for (let i = 0; i < count; i++) {
    next = (next + dir + count) % count;
    if (!disabled.has(next)) return next;
  }
  return clampIndex(current, count);
}

/** Clamp an index into [0, count-1] (count 0 yields 0). */
export function clampIndex(index: number, count: number): number {
  if (count <= 0) return 0;
  return Math.max(0, Math.min(count - 1, index));
}

/**
 * The first selectable index at or after `from` (then wrapping). Used to seat the
 * cursor on a usable row when a menu opens. Falls back to `from` clamped.
 */
export function firstSelectable(
  count: number,
  disabled: ReadonlySet<number> = new Set(),
  from = 0,
): number {
  if (count <= 0) return 0;
  const start = clampIndex(from, count);
  if (!disabled.has(start)) return start;
  for (let i = 1; i < count; i++) {
    const idx = (start + i) % count;
    if (!disabled.has(idx)) return idx;
  }
  return start;
}
